// src/stations/sprint/questEngine.ts — Bexley Quest 11+ Exam Sprint Station
// Implements FR-QUEST-001, FR-QUEST-002, FR-QUEST-003, FR-QUEST-004, NFR-TOUCH-001, NFR-STYLE-001.

import { tokens } from '@ui/designTokens';
import { ClaweyHud } from '@ui/components/claweyHud';
import { SoundEngine } from '@audio/soundEngine';
import { SprintTimer } from './sprintTimer';
import { DiscTimer } from '@ui/components/discTimer';
import { BrainBreak } from '@ui/components/brainBreak';
import { SessionGuard } from '@storage/sessionGuard';
import { TelemetryCollector } from '@storage/telemetryCollector';
import {
  AdaptiveEngine,
  TIER_METAS,
  type DifficultyTier,
} from '@storage/adaptiveEngine';
import {
  SprintStorage,
  SprintSessionRecord,
  SprintAttempt,
  QuestPaperDomain,
} from '@storage/sprintStorage';
import { QuestionPool } from './questionPool';
import { interleaveQuestions, isSubTopicTransition } from './interleaver';
import { WaveTransitionCue } from '@ui/components/waveTransition';
import { AppShell } from '@ui/shell';
import { StreakManager, type ActivityResult } from '@storage/streakManager';
import { StreakCelebration } from '@ui/components/streakCelebration';
import { ExplorerPointsManager, type SpecimenStamp } from '@storage/explorerPoints';
import { SocraticTutorDrawer } from '@ai/socraticTutorDrawer';
import rawQuestions from '@content/questQuestions.json';

export interface QuestQuestion {
  id: string;
  paper: QuestPaperDomain;
  domainName: string;
  topic: string;
  prompt: string;
  contextText?: string;
  type: string;
  svgGraphic?: string;
  options: string[];
  correctAnswer: string;
  explanation: string;
  socraticHints: string[];
  difficulty?: DifficultyTier;
  scaffoldHint?: string;
}

export type SprintViewMode = 'lobby' | 'active' | 'summary';

export class QuestEngine {
  private container: HTMLElement;
  private claweyHud?: ClaweyHud;
  private sound: SoundEngine;
  private timer: SprintTimer | null = null;
  private discTimer: DiscTimer | null = null;

  private allQuestions: QuestQuestion[];
  private activeQuestions: QuestQuestion[] = [];
  private currentQuestionIndex: number = 0;
  private selectedOption: string | null = null;
  private currentStreak: number = 0;
  private bestStreakInSession: number = 0;
  private attempts: SprintAttempt[] = [];
  private activePaper: QuestPaperDomain = 'paper1';
  private viewMode: SprintViewMode = 'lobby';
  private activeMode: 'standard' | 'mistakes_only' = 'standard';
  private currentSetIndex: number = 1;
  private totalSetsInBank: number = 3;
  private questionStartTime: number = 0;
  private hintIndex: number = 0;
  private hasAnsweredCurrent: boolean = false;
  private isCorrectCurrent: boolean = false;
  private isScaffoldActive: boolean = false;
  private scaffoldedOptions: string[] | null = null;
  private usedHintForCurrentQuestion: boolean = false;
  private questionEffortRecords: Array<{
    questionId: string;
    usedHint: boolean;
    isCorrect: boolean;
    tier: DifficultyTier;
    wasPreviousMistake: boolean;
    durationMs: number;
  }> = [];
  private lastStreakResult: ActivityResult | null = null;
  private lastSessionEp: number = 0;
  private lastUnlockedStamps: SpecimenStamp[] = [];
  private drawer?: SocraticTutorDrawer;

  constructor(claweyHud?: ClaweyHud, sound?: SoundEngine) {
    this.container = document.createElement('div');
    this.container.className = 'quest-engine-station';
    this.claweyHud = claweyHud;
    this.sound = sound || new SoundEngine();
    this.allQuestions = rawQuestions as QuestQuestion[];
  }

  render(): HTMLElement {
    this.updateView();
    return this.container;
  }

  private updateView(): void {
    if (this.viewMode === 'lobby') {
      this.renderLobby();
    } else if (this.viewMode === 'active') {
      this.renderActiveSprint();
    } else if (this.viewMode === 'summary') {
      this.renderSummary();
    }
  }

  /** Stage 1: Lobby & Paper Selection */
  private renderLobby(): void {
    AppShell.setFocusMode(false);
    const progress = SprintStorage.load();
    const soundMuted = this.sound.isMuted();

    this.container.innerHTML = `
      <section class="morris-card morris-card-gold" style="padding: 32px 24px; margin-bottom: 24px;">
        <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px; margin-bottom: 18px;">
          <div>
            <div style="display: inline-flex; align-items: center; gap: 8px; font-size: 0.85rem; color: ${tokens.color.seaweedSage}; font-weight: 800; text-transform: uppercase; margin-bottom: 6px;">
              <span>⚡</span> Quest Assessments · Atom Learning Calibration
            </div>
            <h1 style="font-size: 2.2rem; color: ${tokens.color.morrisIndigo}; margin: 0 0 8px;">10-Minute Bexley Quest Sprint Engine</h1>
            <p style="font-size: 1.1rem; color: #3A4D66; max-width: 650px; line-height: 1.6; margin: 0;">
              High-focus, low-anxiety micro-sprints calibrated to the new Bexley 11+ format. Built with Zen Mode to keep Tudor calm and confident.
            </p>
          </div>
          <button id="sprint-sound-toggle" class="touch-target" style="padding: 8px 16px; font-size: 0.95rem; background: #FFFDF9; border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 20px;" aria-label="Toggle sound">
            ${soundMuted ? '🔇 Sound Off' : '🔊 Sound On'}
          </button>
        </div>

        <!-- Telemetry Stats -->
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 14px; margin-top: 20px;">
          <div style="background: rgba(255, 253, 249, 0.9); padding: 14px 18px; border-radius: 12px; border: 1.5px solid rgba(229, 169, 60, 0.4);">
            <div style="font-size: 0.8rem; color: ${tokens.color.seaweedSage}; font-weight: 700;">BEST STREAK</div>
            <div style="font-size: 1.8rem; font-weight: 800; color: ${tokens.color.madderCoral};">🔥 ${progress.bestStreakOverall}</div>
          </div>
          <div style="background: rgba(255, 253, 249, 0.9); padding: 14px 18px; border-radius: 12px; border: 1.5px solid rgba(229, 169, 60, 0.4);">
            <div style="font-size: 0.8rem; color: ${tokens.color.seaweedSage}; font-weight: 700;">SPRINTS COMPLETED</div>
            <div style="font-size: 1.8rem; font-weight: 800; color: ${tokens.color.morrisIndigo};">🏆 ${progress.totalSprintsCompleted}</div>
          </div>
          <div style="background: rgba(255, 253, 249, 0.9); padding: 14px 18px; border-radius: 12px; border: 1.5px solid rgba(229, 169, 60, 0.4);">
            <div style="font-size: 0.8rem; color: ${tokens.color.seaweedSage}; font-weight: 700;">TOTAL ANSWERED</div>
            <div style="font-size: 1.8rem; font-weight: 800; color: ${tokens.color.morrisIndigo};">🎯 ${progress.totalQuestionsAnswered}</div>
          </div>
        </div>
      </section>

      <h2 style="font-size: 1.5rem; color: ${tokens.color.morrisIndigo}; margin: 28px 0 16px;">Choose Your Quest Paper Micro-Sprint:</h2>

      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 20px;">
        <!-- Paper 1 -->
        <div class="morris-card card-interactive" style="border-color: ${tokens.color.gildedBrass};">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
            <span style="font-size: 2rem;">📖</span>
            <span style="font-size: 0.8rem; padding: 4px 10px; border-radius: 6px; background: rgba(229, 169, 60, 0.25); color: ${tokens.color.morrisIndigo}; font-weight: 800;">Paper 1 · 50% Weight</span>
          </div>
          <h3 style="font-size: 1.35rem; margin-bottom: 8px;">Verbal Ability & English</h3>
          <p style="font-size: 0.95rem; color: #3A4D66; margin-bottom: 16px; line-height: 1.6;">
            Vocabulary in marine context, word analogies, hidden words, and deductive verbal reasoning.
          </p>
          <button class="touch-target btn-primary btn-start-paper" data-paper="paper1" style="width: 100%;">
            Start Paper 1 Sprint (10 min) &rarr;
          </button>
        </div>

        <!-- Paper 2: Maths -->
        <div class="morris-card card-interactive" style="border-color: ${tokens.color.gildedBrass};">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
            <span style="font-size: 2rem;">📐</span>
            <span style="font-size: 0.8rem; padding: 4px 10px; border-radius: 6px; background: rgba(0, 229, 255, 0.15); color: ${tokens.color.morrisIndigo}; font-weight: 800;">Paper 2 · 25% Weight</span>
          </div>
          <h3 style="font-size: 1.35rem; margin-bottom: 8px;">Numerical Reasoning</h3>
          <p style="font-size: 0.95rem; color: #3A4D66; margin-bottom: 16px; line-height: 1.6;">
            Mental maths, multi-step word problems, tidal depth rates, ratios, perimeter and factors.
          </p>
          <button class="touch-target btn-gold btn-start-paper" data-paper="paper2_maths" style="width: 100%;">
            Start Numerical Sprint (10 min) &rarr;
          </button>
        </div>

        <!-- Paper 2: NVR & Spatial -->
        <div class="morris-card card-interactive" style="border-color: ${tokens.color.gildedBrass};">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
            <span style="font-size: 2rem;">🧩</span>
            <span style="font-size: 0.8rem; padding: 4px 10px; border-radius: 6px; background: rgba(61, 90, 69, 0.2); color: ${tokens.color.morrisIndigo}; font-weight: 800;">Paper 2 · 25% Weight</span>
          </div>
          <h3 style="font-size: 1.35rem; margin-bottom: 8px;">Non-Verbal & Spatial</h3>
          <p style="font-size: 0.95rem; color: #3A4D66; margin-bottom: 16px; line-height: 1.6;">
            Matrix pattern completion, dot counts, spatial mirror reflection, and 3D cube net folding.
          </p>
          <button class="touch-target btn-start-paper" data-paper="paper2_nvr" style="width: 100%; border: 1.5px solid ${tokens.color.morrisIndigo}; background: #FFFDF9;">
            Start Spatial Sprint (10 min) &rarr;
          </button>
        </div>
      </div>
    `;

    this.attachLobbyListeners();
  }

  private attachLobbyListeners(): void {
    const soundToggle = this.container.querySelector<HTMLButtonElement>('#sprint-sound-toggle');
    soundToggle?.addEventListener('click', () => {
      const isMuted = this.sound.toggleMute();
      soundToggle.textContent = isMuted ? '🔇 Sound Off' : '🔊 Sound On';
      this.sound.playTap();
    });

    const startButtons = this.container.querySelectorAll<HTMLButtonElement>('.btn-start-paper');
    startButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const paper = (btn.getAttribute('data-paper') as QuestPaperDomain) || 'paper1';
        this.sound.playTap();
        this.startSprint(paper);
      });
    });
  }

  /** Start a fresh sprint session with dynamic question pool selection */
  startSprint(
    paper: QuestPaperDomain,
    customDurationSeconds: number = 600,
    mode: 'standard' | 'mistakes_only' = 'standard'
  ): void {
    this.activePaper = paper;
    this.activeMode = mode;

    const poolResult = QuestionPool.selectQuestionSet(this.allQuestions, { paper, mode });
    this.activeQuestions = interleaveQuestions(poolResult.questions);
    this.currentSetIndex = poolResult.setIndex;
    this.totalSetsInBank = poolResult.totalSetsInBank;

    if (this.activeQuestions.length === 0) {
      this.activeQuestions = interleaveQuestions(
        this.allQuestions.filter((q) => q.paper === paper).slice(0, 5)
      );
    }
    this.currentQuestionIndex = 0;
    this.selectedOption = null;
    this.currentStreak = 0;
    this.bestStreakInSession = 0;
    this.attempts = [];
    this.viewMode = 'active';
    this.hintIndex = 0;
    this.hasAnsweredCurrent = false;
    this.isCorrectCurrent = false;
    this.isScaffoldActive = false;
    this.scaffoldedOptions = null;
    this.usedHintForCurrentQuestion = false;
    this.questionEffortRecords = [];
    this.lastStreakResult = null;
    this.lastSessionEp = 0;
    this.lastUnlockedStamps = [];
    this.questionStartTime = Date.now();

    // Initialize countdown timer & visual disc timer
    if (this.timer) {
      this.timer.destroy();
    }
    if (this.discTimer) {
      this.discTimer.destroy();
    }
    this.timer = new SprintTimer(customDurationSeconds, {
      onTick: (_rem, formatted) => {
        const timerEl = this.container.querySelector('#sprint-timer-display');
        if (timerEl && this.timer) {
          timerEl.textContent = this.timer.isZenMode() ? '🧘 Zen Focus' : `⏱️ ${formatted}`;
        }
      },
      onExpire: () => {
        this.sound.playSuccess();
        this.finishSession();
      },
    });

    this.discTimer = new DiscTimer({
      durationSeconds: customDurationSeconds,
      size: 48,
      callbacks: {
        onExpire: () => {
          this.sound.playSuccess();
          this.finishSession();
        },
      },
    });

    this.timer.start();
    this.discTimer.start();
    this.updateView();

    if (this.claweyHud) {
      this.claweyHud.say('Take a deep breath, Tudor! Read each clue carefully. You have all the time you need.', 'idle');
    }
  }

  /** Stage 2: Active Sprint Question View */
  private renderActiveSprint(): void {
    AppShell.setFocusMode(true);

    if (!this.timer || this.currentQuestionIndex >= this.activeQuestions.length) {
      this.finishSession();
      return;
    }

    const question = this.activeQuestions[this.currentQuestionIndex];
    if (!question) {
      this.finishSession();
      return;
    }

    const soundMuted = this.sound.isMuted();
    const isZen = this.timer.isZenMode();
    const progressPercent = Math.round(((this.currentQuestionIndex) / this.activeQuestions.length) * 100);

    const tier = AdaptiveEngine.getQuestionDifficulty(question);
    const tierMeta = TIER_METAS[tier];
    const masteryPercent = AdaptiveEngine.getMasteryPercentage(question.topic);
    const displayedOptions = (this.isScaffoldActive && this.scaffoldedOptions && !this.hasAnsweredCurrent)
      ? this.scaffoldedOptions
      : question.options;

    this.container.innerHTML = `
      <!-- Station Top Control Bar -->
      <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; padding: 12px 18px; background: rgba(255, 253, 249, 0.95); border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 16px; box-shadow: 0 4px 16px rgba(7, 20, 36, 0.08);">
        <div style="display: flex; align-items: center; gap: 10px;">
          <span style="font-weight: 800; color: ${tokens.color.morrisIndigo}; font-size: 1.05rem;">
            ${question.domainName}
          </span>
          <span style="font-size: 0.85rem; padding: 3px 10px; border-radius: 12px; background: rgba(229, 169, 60, 0.2); color: ${tokens.color.morrisIndigo}; font-weight: 700;">
            ${this.activeMode === 'mistakes_only' ? '🎯 Mistake Review' : `Set ${this.currentSetIndex} of ${this.totalSetsInBank}`} · Question ${this.currentQuestionIndex + 1} of ${this.activeQuestions.length}
          </span>
        </div>

        <div style="display: flex; align-items: center; gap: 10px;">
          <!-- Streak Indicator -->
          <span id="sprint-streak-badge" style="display: inline-flex; align-items: center; gap: 6px; font-size: 0.95rem; font-weight: 800; color: ${tokens.color.madderCoral}; padding: 6px 14px; background: rgba(255, 107, 74, 0.12); border-radius: 20px;">
            🔥 Streak: ${this.currentStreak}
          </span>

          <!-- Zen Mode Toggle -->
          <button id="sprint-zen-toggle" class="touch-target" style="padding: 6px 14px; font-size: 0.9rem; border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 20px; background: ${isZen ? '#E5A93C' : '#FFFDF9'}; color: ${tokens.color.morrisIndigo}; font-weight: 800;" title="Toggle Zen Mode (hides clock)">
            ${isZen ? '🧘 Zen: ON' : '⏱️ Zen: OFF'}
          </button>

          <!-- Visual Disc Timer Mount (FR-ADHD-001) -->
          <div id="sprint-disc-mount" style="display: flex; align-items: center; justify-content: center; min-width: 48px; min-height: 48px;"></div>

          <!-- Timer Display -->
          <div id="sprint-timer-display" style="font-family: monospace; font-size: 1.15rem; font-weight: 800; color: ${tokens.color.morrisIndigo}; min-width: 90px; text-align: center;">
            ${isZen ? '🧘 Zen Focus' : `⏱️ ${this.timer.getFormattedTime()}`}
          </div>

          <!-- Sound Toggle -->
          <button id="sprint-sound-toggle-active" class="touch-target" style="padding: 6px 12px; font-size: 0.9rem; border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 20px; background: #FFFDF9;">
            ${soundMuted ? '🔇' : '🔊'}
          </button>

          <!-- Quit / End Sprint (hidden during active answering to minimize cognitive distraction) -->
          <button id="sprint-quit-btn" class="touch-target focus-hidden-during-answering" style="padding: 6px 14px; font-size: 0.85rem; border: 1px solid rgba(168, 50, 50, 0.4); border-radius: 20px; background: rgba(255, 235, 235, 0.7); color: #A83232; font-weight: 700;">
            Finish Early
          </button>
        </div>
      </div>

      <!-- Linear Progress Bar -->
      <div style="height: 6px; width: 100%; background: rgba(229, 169, 60, 0.2); border-radius: 3px; overflow: hidden; margin-bottom: 20px;">
        <div style="height: 100%; width: ${progressPercent}%; background: ${tokens.color.gildedBrass}; transition: width 0.3s ease;"></div>
      </div>

      <!-- Question Morris Stage Card -->
      <section class="morris-card" style="padding: 32px 28px; margin-bottom: 24px; position: relative;">
        <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 12px;">
          <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
            <span style="font-size: 0.85rem; padding: 4px 12px; border-radius: 12px; background: rgba(7, 20, 36, 0.08); color: ${tokens.color.morrisIndigo}; font-weight: 800; text-transform: uppercase;">
              ${question.topic}
            </span>
            <!-- Difficulty Tier & Mastery Badge (FR-ADAPT-001) -->
            <span id="question-difficulty-badge" style="font-size: 0.8rem; padding: 3px 10px; border-radius: 12px; background: ${tierMeta.bg}; color: ${tierMeta.color}; font-weight: 800; border: 1px solid ${tierMeta.color};">
              ${tierMeta.badge} · ${masteryPercent}% Mastery
            </span>
          </div>
          <span style="font-size: 0.85rem; color: ${tokens.color.seaweedSage}; font-style: italic;">
            Select one option
          </span>
        </div>

        ${(this.isScaffoldActive && this.scaffoldedOptions && !this.hasAnsweredCurrent) ? `
          <div id="scaffold-active-banner" style="margin-bottom: 16px; padding: 10px 14px; border-radius: 10px; background: rgba(0, 229, 255, 0.12); border-left: 3.5px solid ${tokens.color.biolumCyan}; font-size: 0.9rem; color: ${tokens.color.morrisIndigo}; font-weight: 700; display: flex; align-items: center; gap: 8px;">
            <span>🪸</span>
            <span>Clawey Scaffolding Active: 2 unlikely choices cleared away to sharpen your focus!</span>
          </div>
        ` : ''}

        <h2 style="font-size: 1.45rem; line-height: 1.5; color: ${tokens.color.morrisIndigo}; margin: 0 0 16px;">
          ${question.prompt}
        </h2>

        ${
          question.contextText
            ? `<div style="padding: 14px 18px; background: rgba(255, 253, 249, 0.9); border-left: 4px solid ${tokens.color.gildedBrass}; margin-bottom: 20px; font-style: italic; font-size: 1.05rem; line-height: 1.6; color: #2D3E50;">
                ${question.contextText}
              </div>`
            : ''
        }

        ${
          question.svgGraphic
            ? `<div style="display: flex; justify-content: center; margin: 16px 0 24px; padding: 16px; background: rgba(255, 253, 249, 0.9); border: 1.5px solid rgba(229, 169, 60, 0.4); border-radius: 12px;">
                ${question.svgGraphic}
              </div>`
            : ''
        }

        <!-- Interactive Options Grid -->
        <div class="sprint-options-grid" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 14px; margin-top: 20px;">
          ${displayedOptions
            .map(
              (opt, idx) => `
            <button
              class="sprint-option-btn touch-target"
              data-option="${this.escapeHtml(opt)}"
              style="
                padding: 18px 20px;
                text-align: left;
                font-size: 1.1rem;
                font-weight: 700;
                line-height: 1.4;
                border-radius: 14px;
                border: 2px solid ${this.selectedOption === opt ? tokens.color.gildedBrass : 'rgba(7, 20, 36, 0.15)'};
                background: ${this.selectedOption === opt ? 'rgba(255, 246, 229, 0.95)' : '#FFFDF9'};
                color: ${tokens.color.morrisIndigo};
                cursor: pointer;
                transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
                display: flex;
                align-items: center;
                gap: 12px;
                min-height: 56px;
              "
              ${this.hasAnsweredCurrent ? 'disabled' : ''}
            >
              <span style="display: inline-flex; justify-content: center; align-items: center; width: 32px; height: 32px; border-radius: 50%; background: ${this.selectedOption === opt ? tokens.color.gildedBrass : 'rgba(7, 20, 36, 0.08)'}; color: ${this.selectedOption === opt ? '#FFFDF9' : tokens.color.morrisIndigo}; font-size: 0.9rem; font-weight: 800; flex-shrink: 0;">
                ${String.fromCharCode(65 + idx)}
              </span>
              <span>${this.escapeHtml(opt)}</span>
            </button>
          `
            )
            .join('')}
        </div>

        <!-- Feedback & Socratic Hint Tray -->
        <div id="sprint-feedback-tray" style="margin-top: 24px; min-height: 60px;">
          ${
            this.hasAnsweredCurrent
              ? `
              <div style="padding: 16px 20px; border-radius: 14px; border: 1.5px solid ${this.isCorrectCurrent ? 'rgba(61, 90, 69, 0.5)' : 'rgba(255, 107, 74, 0.5)'}; background: ${this.isCorrectCurrent ? 'rgba(235, 245, 238, 0.95)' : 'rgba(255, 246, 244, 0.95)'};">
                <div style="font-weight: 800; font-size: 1.15rem; color: ${this.isCorrectCurrent ? tokens.color.seaweedSage : tokens.color.madderCoral}; margin-bottom: 6px;">
                  ${this.isCorrectCurrent ? '✨ Brilliant Reasoning, Tudor!' : '🌊 Epic Try! Clawey\'s Field Note:'}
                </div>
                <p style="margin: 0 0 14px; font-size: 1rem; line-height: 1.5; color: #2D3E50;">
                  ${this.isCorrectCurrent ? question.explanation : question.socraticHints[Math.min(this.hintIndex, question.socraticHints.length - 1)]}
                </p>
                <button id="sprint-next-btn" class="touch-target btn-primary" style="padding: 12px 28px; font-size: 1.05rem;">
                  ${this.currentQuestionIndex + 1 < this.activeQuestions.length ? 'Next Question &rarr;' : 'View Sprint Results &rarr;'}
                </button>
              </div>
            `
              : `
              <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 14px;">
                <button id="sprint-hint-btn" class="touch-target" style="padding: 10px 18px; font-size: 0.95rem; border: 1.5px dashed ${tokens.color.gildedBrass}; border-radius: 20px; background: #FFFDF9; color: ${tokens.color.morrisIndigo}; font-weight: 700;">
                  💡 Need a Clawey Nudge?
                </button>
                <button id="sprint-submit-btn" class="touch-target btn-primary" style="padding: 14px 32px; font-size: 1.1rem;" ${!this.selectedOption ? 'disabled' : ''}>
                  Submit Answer &rarr;
                </button>
              </div>
            `
          }
        </div>
      </section>
    `;

    // Mount Visual Disc Timer (FR-ADHD-001)
    const discMount = this.container.querySelector<HTMLElement>('#sprint-disc-mount');
    if (discMount && this.discTimer) {
      discMount.innerHTML = '';
      discMount.appendChild(this.discTimer.getElement());
    }

    this.attachActiveListeners();
  }

  private attachActiveListeners(): void {
    // Sound toggle
    const soundToggle = this.container.querySelector<HTMLButtonElement>('#sprint-sound-toggle-active');
    soundToggle?.addEventListener('click', () => {
      const isMuted = this.sound.toggleMute();
      soundToggle.textContent = isMuted ? '🔇' : '🔊';
      this.sound.playTap();
    });

    // Zen Mode toggle
    const zenToggle = this.container.querySelector<HTMLButtonElement>('#sprint-zen-toggle');
    zenToggle?.addEventListener('click', () => {
      if (this.timer) {
        const isZen = this.timer.toggleZenMode();
        if (this.discTimer) {
          this.discTimer.setZenMode(isZen);
        }
        zenToggle.textContent = isZen ? '🧘 Zen: ON' : '⏱️ Zen: OFF';
        zenToggle.style.background = isZen ? '#E5A93C' : '#FFFDF9';
        const timerEl = this.container.querySelector('#sprint-timer-display');
        if (timerEl) {
          timerEl.textContent = isZen ? '🧘 Zen Focus' : `⏱️ ${this.timer.getFormattedTime()}`;
        }
        this.sound.playTap();
      }
    });

    // Quit early
    const quitBtn = this.container.querySelector<HTMLButtonElement>('#sprint-quit-btn');
    quitBtn?.addEventListener('click', () => {
      this.sound.playTap();
      this.finishSession();
    });

    // Option selection
    const optionButtons = this.container.querySelectorAll<HTMLButtonElement>('.sprint-option-btn');
    optionButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (this.hasAnsweredCurrent) return;
        this.selectedOption = btn.getAttribute('data-option');
        this.sound.playTap();
        this.updateView();
      });
    });

    // Submit Answer
    const submitBtn = this.container.querySelector<HTMLButtonElement>('#sprint-submit-btn');
    submitBtn?.addEventListener('click', () => {
      this.submitCurrentAnswer();
    });

    // Socratic Hint request
    const hintBtn = this.container.querySelector<HTMLButtonElement>('#sprint-hint-btn');
    hintBtn?.addEventListener('click', () => {
      this.usedHintForCurrentQuestion = true;
      const question = this.activeQuestions[this.currentQuestionIndex];
      if (question) {
        this.sound.playTap();
        if (question.socraticHints.length > 0) {
          const hintText = question.socraticHints[this.hintIndex % question.socraticHints.length];
          this.hintIndex += 1;
          if (this.claweyHud) {
            this.claweyHud.say(hintText ?? 'Look closely at the pattern!', 'thinking');
          }
        }

        if (!this.drawer) {
          this.drawer = new SocraticTutorDrawer();
          this.container.appendChild(this.drawer.getElement());
        }
        this.drawer.openWithRequest({
          questionId: question.id,
          domain: question.paper,
          topic: question.topic,
          prompt: question.prompt,
          contextText: question.contextText,
          options: question.options,
          selectedAnswer: this.selectedOption,
        });
      }
    });

    // Next Question
    const nextBtn = this.container.querySelector<HTMLButtonElement>('#sprint-next-btn');
    nextBtn?.addEventListener('click', () => {
      this.sound.playTap();
      this.advanceToNextQuestion();
    });
  }

  /** Validate and process answer */
  private submitCurrentAnswer(): void {
    if (!this.selectedOption || this.hasAnsweredCurrent) return;
    const question = this.activeQuestions[this.currentQuestionIndex];
    if (!question) return;

    const secondsSpent = Math.max(1, Math.round((Date.now() - this.questionStartTime) / 1000));
    const isCorrect = this.selectedOption === question.correctAnswer;
    this.hasAnsweredCurrent = true;
    this.isCorrectCurrent = isCorrect;

    // Record attempt
    this.attempts.push({
      questionId: question.id,
      userAnswer: this.selectedOption,
      correct: isCorrect,
      secondsSpent,
    });

    const sessionElapsed = this.timer ? (this.timer.getTotalDuration() - this.timer.getRemaining()) : 0;
    const durationMs = Date.now() - this.questionStartTime;
    TelemetryCollector.recordEvent({
      questionId: question.id,
      domain: question.paper,
      topic: question.topic,
      durationMs,
      correct: isCorrect,
      sessionElapsedSeconds: sessionElapsed,
    });

    // Record effort for Explorer Points (FR-EFFORT-001)
    const isPreviousMistake = SprintStorage.isQuestionIncorrect(question.id);
    const tier = AdaptiveEngine.getQuestionDifficulty(question);
    this.questionEffortRecords.push({
      questionId: question.id,
      usedHint: this.usedHintForCurrentQuestion,
      isCorrect,
      tier,
      wasPreviousMistake: isPreviousMistake,
      durationMs,
    });

    if (isCorrect) {
      this.currentStreak += 1;
      if (this.currentStreak > this.bestStreakInSession) {
        this.bestStreakInSession = this.currentStreak;
      }
      SprintStorage.incrementStreak();

      // Audio feedback
      if (this.currentStreak % 3 === 0) {
        this.sound.playStreak();
      } else {
        this.sound.playSuccess();
      }

      if (this.claweyHud) {
        this.claweyHud.say(`Spot on, Tudor! ${this.currentStreak > 1 ? `You're on a ${this.currentStreak} streak!` : ''}`, 'celebrating');
      }
    } else {
      this.currentStreak = 0;
      SprintStorage.resetStreak();
      this.sound.playTryAgain();

      if (this.claweyHud) {
        const hint = question.socraticHints[0] || 'Take another look at the details.';
        this.claweyHud.say(hint, 'thinking');
      }
    }

    // Record in Adaptive Learning Engine (FR-ADAPT-001, FR-ADAPT-002, FR-ADAPT-003)
    const adaptResult = AdaptiveEngine.recordAttempt(question.topic, isCorrect);
    this.isScaffoldActive = adaptResult.isScaffoldRecommended;

    if (adaptResult.tierTransition === 'escalated' && this.claweyHud) {
      this.claweyHud.say(`Splendid, Tudor! Your mastery of ${question.topic} elevated you to ${adaptResult.record.currentTier.toUpperCase()} tier!`, 'celebrating');
    }

    this.updateView();
  }

  /** Proceed to next question or finalize sprint */
  private advanceToNextQuestion(): void {
    const prevQ = this.activeQuestions[this.currentQuestionIndex] ?? null;
    this.currentQuestionIndex += 1;
    this.selectedOption = null;
    this.hasAnsweredCurrent = false;
    this.isCorrectCurrent = false;
    this.hintIndex = 0;
    this.usedHintForCurrentQuestion = false;
    this.questionStartTime = Date.now();

    if (this.currentQuestionIndex >= this.activeQuestions.length) {
      this.finishSession();
    } else {
      const nextQ = this.activeQuestions[this.currentQuestionIndex];
      if (nextQ && isSubTopicTransition(prevQ, nextQ)) {
        const cue = new WaveTransitionCue({
          newSubTopic: nextQ.topic,
          domainName: nextQ.domainName,
        });
        cue.show(this.container);
      }
      if (nextQ && this.isScaffoldActive) {
        this.scaffoldedOptions = AdaptiveEngine.scaffoldOptions(nextQ.options, nextQ.correctAnswer);
      } else {
        this.scaffoldedOptions = null;
      }
      this.updateView();
    }
  }

  /** Stage 3: Summary / Results Stage */
  private finishSession(): void {
    AppShell.setFocusMode(false);

    if (this.timer) {
      this.timer.pause();
    }
    if (this.discTimer) {
      this.discTimer.pause();
    }
    this.viewMode = 'summary';

    const durationSeconds = this.timer ? (this.timer.getTotalDuration() - this.timer.getRemaining()) : 0;
    SessionGuard.recordPracticeTime(durationSeconds);

    // Record Streak Activity with 48h Grace Freeze (FR-ADHD-005)
    this.lastStreakResult = StreakManager.recordActivity();
    if (this.lastStreakResult.milestoneHit) {
      const celebration = new StreakCelebration({
        milestone: this.lastStreakResult.milestoneHit,
      });
      document.body.appendChild(celebration.getElement());
    }

    // Evaluate Effort-Based Explorer Points & Specimen Stamps (FR-EFFORT-001, FR-MAP-002)
    const effortEval = ExplorerPointsManager.evaluateSession(this.questionEffortRecords);
    this.lastSessionEp = effortEval.sessionEp;
    this.lastUnlockedStamps = effortEval.newStampsUnlocked;

    const totalQuestions = this.attempts.length > 0 ? this.attempts.length : this.activeQuestions.length;
    const correctCount = this.attempts.filter((a) => a.correct).length;
    const accuracyPercent = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;
    const estimatedSas = SprintStorage.calculateQuestSas(correctCount, totalQuestions);

    const sessionRecord: SprintSessionRecord = {
      id: `sprint_${Date.now()}`,
      paper: this.activePaper,
      paperTitle: this.activePaper === 'paper1' ? 'Paper 1: Verbal & English' : this.activePaper === 'paper2_maths' ? 'Paper 2: Numerical' : 'Paper 2: Non-Verbal & Spatial',
      totalQuestions,
      correctCount,
      accuracyPercent,
      estimatedSas,
      bestStreak: this.bestStreakInSession,
      completed: true,
      durationSeconds,
      timestamp: Date.now(),
      attempts: [...this.attempts],
    };

    SprintStorage.recordSession(sessionRecord);
    this.renderSummary(sessionRecord);
  }

  private renderSummary(record?: SprintSessionRecord): void {
    const totalQuestions = record?.totalQuestions ?? this.attempts.length;
    const correctCount = record?.correctCount ?? this.attempts.filter((a) => a.correct).length;
    const accuracy = record?.accuracyPercent ?? (totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0);
    const sas = record?.estimatedSas ?? SprintStorage.calculateQuestSas(correctCount, totalQuestions);
    const streak = record?.bestStreak ?? this.bestStreakInSession;

    const nextPool = QuestionPool.selectQuestionSet(this.allQuestions, { paper: this.activePaper });
    const nextSetIndex = nextPool.setIndex;
    const availableMistakes = SprintStorage.getIncorrectQuestionIds(this.activePaper).length;

    this.container.innerHTML = `
      <section class="morris-card morris-card-gold" style="text-align: center; padding: 40px 24px; margin-bottom: 24px;">
        <div style="font-size: 3rem; margin-bottom: 8px;">🌟</div>
        <div style="display: inline-flex; align-items: center; gap: 8px; font-size: 0.85rem; color: ${tokens.color.seaweedSage}; font-weight: 800; text-transform: uppercase; margin-bottom: 6px;">
          <span>✓</span> Set ${this.currentSetIndex} of ${this.totalSetsInBank} Completed
        </div>
        <h1 style="font-size: 2.3rem; color: ${tokens.color.morrisIndigo}; margin: 0 0 12px;">Quest Sprint Completed!</h1>
        <p style="font-size: 1.15rem; color: #3A4D66; max-width: 580px; margin: 0 auto 28px; line-height: 1.6;">
          Tremendous expedition work, Tudor! Your calm focus during this sprint moves you closer to conquering the Bexley Selection Test.
        </p>

        <!-- Metric Scoreboard -->
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 16px; max-width: 760px; margin: 0 auto 28px;">
          <div style="background: rgba(255, 253, 249, 0.95); padding: 18px; border-radius: 14px; border: 1.5px solid ${tokens.color.gildedBrass};">
            <div style="font-size: 0.85rem; color: ${tokens.color.seaweedSage}; font-weight: 700;">ACCURACY</div>
            <div style="font-size: 2.2rem; font-weight: 800; color: ${tokens.color.morrisIndigo};">${accuracy}%</div>
            <div style="font-size: 0.85rem; color: #5A6D86;">${correctCount} of ${totalQuestions} Correct</div>
          </div>

          <div style="background: rgba(255, 253, 249, 0.95); padding: 18px; border-radius: 14px; border: 1.5px solid ${tokens.color.gildedBrass};">
            <div style="font-size: 0.85rem; color: ${tokens.color.seaweedSage}; font-weight: 700;">ESTIMATED QUEST SAS</div>
            <div style="font-size: 2.2rem; font-weight: 800; color: ${tokens.color.morrisIndigo};">${sas}</div>
            <div style="font-size: 0.85rem; color: ${tokens.color.seaweedSage}; font-weight: 700;">Mean 200 Scale</div>
          </div>

          <div style="background: rgba(255, 253, 249, 0.95); padding: 18px; border-radius: 14px; border: 1.5px solid ${tokens.color.gildedBrass};">
            <div style="font-size: 0.85rem; color: ${tokens.color.seaweedSage}; font-weight: 700;">BEST STREAK</div>
            <div style="font-size: 2.2rem; font-weight: 800; color: ${tokens.color.madderCoral};">🔥 ${streak}</div>
            <div style="font-size: 0.85rem; color: #5A6D86;">Consecutive Correct</div>
          </div>

          <div style="background: rgba(255, 253, 249, 0.95); padding: 18px; border-radius: 14px; border: 1.5px solid ${tokens.color.gildedBrass};">
            <div style="font-size: 0.85rem; color: ${tokens.color.seaweedSage}; font-weight: 700;">EXPLORER POINTS</div>
            <div style="font-size: 2.2rem; font-weight: 800; color: ${tokens.color.gildedBrass};">🧭 +${this.lastSessionEp} EP</div>
            <div style="font-size: 0.85rem; color: #5A6D86;">${ExplorerPointsManager.getTotalEp()} Total EP</div>
          </div>
        </div>

        ${this.lastStreakResult ? `
          <div style="margin-bottom: 20px; padding: 10px 18px; background: rgba(255, 246, 229, 0.95); border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 14px; display: inline-flex; align-items: center; gap: 10px; font-size: 0.95rem; color: ${tokens.color.morrisIndigo}; font-weight: 800;">
            <span>🌊</span>
            <span>Tudor's Practice Voyage: ${this.lastStreakResult.currentStreak} Day${this.lastStreakResult.currentStreak > 1 ? 's' : ''}!</span>
            ${this.lastStreakResult.resumedFromFreeze ? '<span style="font-size: 0.8rem; padding: 2px 8px; border-radius: 10px; background: rgba(0, 229, 255, 0.15); color: #007A8A;">⚓ 48h Grace Freeze Protected</span>' : ''}
          </div>
        ` : ''}

        ${this.lastUnlockedStamps.length > 0 ? `
          <div style="margin-bottom: 24px; padding: 16px 20px; background: linear-gradient(135deg, rgba(255, 246, 229, 0.98), rgba(255, 235, 190, 0.95)); border: 2px solid ${tokens.color.gildedBrass}; border-radius: 16px; box-shadow: 0 4px 16px rgba(229, 169, 60, 0.25); max-width: 600px; margin-left: auto; margin-right: auto;">
            <div style="font-size: 0.85rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase; margin-bottom: 4px;">
              🎉 New Marine Specimen Stamp Unlocked!
            </div>
            ${this.lastUnlockedStamps.map(s => `
              <div style="font-size: 1.15rem; font-weight: 800; color: ${tokens.color.morrisIndigo};">
                ${s.icon} ${s.name} <span style="font-size: 0.9rem; font-weight: 700; color: #5A6D86;">(${s.habitat} · ${s.epThreshold} EP Milestone)</span>
              </div>
            `).join('')}
          </div>
        ` : ''}

        <!-- Set Progression Note -->
        <div style="margin-bottom: 24px; padding: 12px 18px; background: rgba(61, 90, 69, 0.1); border-radius: 12px; display: inline-block; font-size: 0.95rem; color: ${tokens.color.morrisIndigo}; font-weight: 700;">
          ${nextPool.unseenRemaining > 0 
            ? `🌱 <strong>${nextPool.unseenRemaining} new questions</strong> ready in this paper domain!` 
            : `🏆 Full question bank conquered! Next set will smartly review earlier questions.`}
        </div>

        <div style="display: flex; justify-content: center; gap: 16px; flex-wrap: wrap;">
          <button id="sprint-next-set-btn" class="touch-target btn-primary" style="padding: 14px 28px; font-size: 1.1rem;">
            ⚡ Start Next Set (Set ${nextSetIndex}) &rarr;
          </button>
          ${availableMistakes > 0 ? `
          <button id="sprint-mistakes-btn" class="touch-target btn-gold" style="padding: 14px 28px; font-size: 1.1rem;">
            🎯 Review Mistakes (${availableMistakes} Question${availableMistakes > 1 ? 's' : ''})
          </button>
          ` : ''}
          <button id="sprint-brain-break-btn" class="touch-target" style="padding: 14px 24px; font-size: 1rem; border: 1.5px solid ${tokens.color.seaweedSage}; background: rgba(61, 90, 69, 0.08); color: ${tokens.color.seaweedSage}; border-radius: 14px; font-weight: 800;">
            🧘 30s Brain Break
          </button>
          <button id="sprint-retry-btn" class="touch-target" style="padding: 14px 24px; font-size: 1rem; border: 1.5px solid ${tokens.color.morrisIndigo}; background: #FFFDF9; border-radius: 14px;">
            🔄 Replay Set ${this.currentSetIndex}
          </button>
          <button id="sprint-switch-btn" class="touch-target" style="padding: 14px 24px; font-size: 1rem; border: 1.5px solid rgba(26, 54, 93, 0.3); background: #FFFDF9; border-radius: 14px;">
            📑 Choose Another Paper
          </button>
          <a href="#/" class="touch-target" style="padding: 14px 24px; font-size: 1rem; border: 1.5px solid ${tokens.color.morrisIndigo}; background: #FFFDF9; border-radius: 14px; text-decoration: none; display: inline-flex; align-items: center;">
            🗺️ Back to Map
          </a>
        </div>
      </section>
    `;

    this.attachSummaryListeners();

    // Check if daily practice limit reached (FR-ADHD-003)
    if (SessionGuard.isDailyLimitReached()) {
      const restModal = SessionGuard.createRestWarningModal(() => {});
      document.body.appendChild(restModal);
    }
  }

  private attachSummaryListeners(): void {
    const nextSetBtn = this.container.querySelector<HTMLButtonElement>('#sprint-next-set-btn');
    nextSetBtn?.addEventListener('click', () => {
      this.sound.playTap();
      this.startSprint(this.activePaper, 600, 'standard');
    });

    const brainBreakBtn = this.container.querySelector<HTMLButtonElement>('#sprint-brain-break-btn');
    brainBreakBtn?.addEventListener('click', () => {
      this.sound.playTap();
      const brainBreak = new BrainBreak({
        onComplete: () => {
          if (this.claweyHud) {
            this.claweyHud.say('Mind refreshed! Ready for the next expedition, Tudor.', 'celebrating');
          }
        },
      });
      document.body.appendChild(brainBreak.getElement());
    });

    const mistakesBtn = this.container.querySelector<HTMLButtonElement>('#sprint-mistakes-btn');
    mistakesBtn?.addEventListener('click', () => {
      this.sound.playTap();
      this.startSprint(this.activePaper, 600, 'mistakes_only');
    });

    const retryBtn = this.container.querySelector<HTMLButtonElement>('#sprint-retry-btn');
    retryBtn?.addEventListener('click', () => {
      this.sound.playTap();
      this.startSprint(this.activePaper, 600, 'standard');
    });

    const switchBtn = this.container.querySelector<HTMLButtonElement>('#sprint-switch-btn');
    switchBtn?.addEventListener('click', () => {
      this.sound.playTap();
      this.viewMode = 'lobby';
      this.updateView();
    });
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /** Helper to retrieve the current DOM container */
  getElement(): HTMLElement {
    return this.container;
  }
}
